/* Feedback op documenten (migratie 098, ADR 0021): punten per Versie en plek, een draad per
 * punt, en het feedbackverzoek (de "ping") met taak, melding en push.
 *
 * Eén model voor het CRM en de iPhone-app. Lezen gaat in één aanroep
 * (document_feedback_overview), schrijven alleen via de RPC's; de database controleert de
 * rol. Er gaat niets naar klanten: om feedback vragen kan alleen aan interne teamleden.
 *
 * Een punt hangt aan de Versie waarop het geplaatst is (file_id). De punten van een document
 * zijn de punten op één van zijn bestanden, zodat open punten van v3 zichtbaar blijven bij v4.
 * De plek is een fractie van de pagina zoals hij getoond wordt, vanaf linksboven. */

import type { DocumentGroup } from '../../../../supabase/functions/_shared/fileVersions';
import { roepAan, type Bestand, type BestandenClient } from './bestanden';

interface RpcFout {
  message?: string;
  code?: string;
}

export interface FeedbackClient extends BestandenClient {
  rpc(functie: string, args?: Record<string, unknown>): PromiseLike<{ data: unknown; error: RpcFout | null }>;
}

export type FeedbackStatus = 'open' | 'resolved' | 'dismissed';

export interface Reactie {
  id: string;
  kind: 'reply' | 'status';
  body: string;
  author_id: string | null;
  author_name: string | null;
  created_at: string;
}

export interface FeedbackPunt {
  id: string;
  file_id: string;
  file_name: string;
  folder: string;
  version: string | null;
  page: number | null;
  x: number | null;
  y: number | null;
  w: number | null;
  h: number | null;
  body: string;
  status: FeedbackStatus;
  resolved_file_id: string | null;
  resolved_file_name: string | null;
  resolved_version: string | null;
  resolved_by: string | null;
  resolved_by_name: string | null;
  resolved_at: string | null;
  request_id: string | null;
  author_id: string | null;
  author_name: string | null;
  created_at: string;
  replies: Reactie[];
}

export type PushStatus = 'pending' | 'sent' | 'no_device' | 'failed' | 'not_configured';

export interface Ontvanger {
  user_id: string;
  name: string | null;
  done: boolean;
  done_at: string | null;
  todo_id: string | null;
  push_status: PushStatus;
  push_at: string | null;
}

export interface FeedbackVerzoek {
  id: string;
  file_id: string;
  file_name: string;
  folder: string;
  version: string | null;
  document_title: string | null;
  question: string | null;
  due_on: string | null;
  requested_by: string | null;
  requested_by_name: string | null;
  created_at: string;
  recipients: Ontvanger[];
}

export interface FeedbackOverzicht {
  /** false zolang migratie 098 niet is toegepast: dan toont de pagina geen feedback. */
  beschikbaar: boolean;
  punten: FeedbackPunt[];
  verzoeken: FeedbackVerzoek[];
}

export interface Mens {
  user_id: string;
  name: string;
  email: string;
  is_me: boolean;
  has_device: boolean;
}

/** De Versie waar een punt of verzoek aan hangt. */
export interface VersieRef {
  file_id: string;
  file_name: string;
  folder: string;
  version: string | null;
}

/** Plek op de pagina: niets (hele document), alleen een pagina/dia, een punt of een kader. */
export interface Anker {
  page: number | null;
  x?: number | null;
  y?: number | null;
  w?: number | null;
  h?: number | null;
}

/** Een ontbrekende functie (migratie nog niet toegepast) is geen fout maar "nog niet". */
export function isOntbrekendeFunctie(error: RpcFout | null | undefined): boolean {
  const code = String(error?.code ?? '');
  return code === 'PGRST202' || code === '42883' || /could not find the function/i.test(String(error?.message ?? ''));
}

function fout(error: RpcFout): Error {
  return new Error(error.message ?? String(error));
}

export async function haalFeedback(client: FeedbackClient, projectId: string): Promise<FeedbackOverzicht> {
  const { data, error } = await client.rpc('document_feedback_overview', { p_project_id: projectId });
  if (error) {
    if (isOntbrekendeFunctie(error)) return { beschikbaar: false, punten: [], verzoeken: [] };
    throw fout(error);
  }
  const o = (data ?? {}) as { feedback?: FeedbackPunt[]; requests?: FeedbackVerzoek[] };
  return { beschikbaar: true, punten: o.feedback ?? [], verzoeken: o.requests ?? [] };
}

export async function haalMensen(client: FeedbackClient): Promise<Mens[]> {
  const { data, error } = await client.rpc('feedback_people');
  if (error) {
    if (isOntbrekendeFunctie(error)) return [];
    throw fout(error);
  }
  return (data ?? []) as Mens[];
}

const rond = (v: number | null | undefined) => (v === null || v === undefined ? null : Math.round(v * 10000) / 10000);

export async function plaatsPunt(
  client: FeedbackClient,
  v: { projectId: string; versie: VersieRef; tekst: string; anker: Anker; verzoekId?: string | null },
): Promise<FeedbackPunt> {
  const { data, error } = await client.rpc('feedback_add', {
    p_project_id: v.projectId,
    p_file_id: v.versie.file_id,
    p_file_name: v.versie.file_name,
    p_folder: v.versie.folder,
    p_version: v.versie.version,
    p_body: v.tekst.trim(),
    p_page: v.anker.page ?? null,
    p_x: rond(v.anker.x),
    p_y: rond(v.anker.y),
    p_w: rond(v.anker.w),
    p_h: rond(v.anker.h),
    p_request_id: v.verzoekId ?? null,
  });
  if (error) throw fout(error);
  return data as FeedbackPunt;
}

export async function reageer(client: FeedbackClient, puntId: string, tekst: string): Promise<void> {
  const { error } = await client.rpc('feedback_reply', { p_feedback_id: puntId, p_body: tekst.trim() });
  if (error) throw fout(error);
}

export async function zetStatus(
  client: FeedbackClient,
  puntId: string,
  status: FeedbackStatus,
  opties: { notitie?: string; versie?: VersieRef | null } = {},
): Promise<void> {
  const { error } = await client.rpc('feedback_set_status', {
    p_feedback_id: puntId,
    p_status: status,
    p_note: opties.notitie?.trim() || null,
    p_file_id: opties.versie?.file_id ?? null,
    p_file_name: opties.versie?.file_name ?? null,
    p_version: opties.versie?.version ?? null,
  });
  if (error) throw fout(error);
}

export async function haalPuntWeg(client: FeedbackClient, puntId: string): Promise<void> {
  const { error } = await client.rpc('feedback_remove', { p_feedback_id: puntId });
  if (error) throw fout(error);
}

export async function klaarMetFeedback(client: FeedbackClient, verzoekId: string): Promise<void> {
  const { error } = await client.rpc('feedback_request_done', { p_request_id: verzoekId });
  if (error) throw fout(error);
}

export interface PushUitkomst {
  configured: boolean;
  recipients: { user_id: string; push_status: PushStatus; devices: number; detail: string | null }[];
}

export interface PingUitkomst {
  verzoekId: string;
  /** null als de push-functie niet bereikbaar was; melding en taak staan er dan wel. */
  push: PushUitkomst | null;
  pushFout: string | null;
}

/** De ping: verzoek + taak + melding in de database, daarna de push via apns-push. */
export async function vraagFeedback(
  client: FeedbackClient,
  v: { projectId: string; versie: VersieRef; titel: string; vraag: string; deadline: string | null; personen: string[] },
): Promise<PingUitkomst> {
  const { data, error } = await client.rpc('feedback_request_create', {
    p_project_id: v.projectId,
    p_file_id: v.versie.file_id,
    p_file_name: v.versie.file_name,
    p_folder: v.versie.folder,
    p_version: v.versie.version,
    p_document_title: v.titel,
    p_question: v.vraag.trim() || null,
    p_due_on: v.deadline || null,
    p_recipients: v.personen,
  });
  if (error) throw fout(error);
  const verzoekId = (data as { id: string }).id;
  const push = await client.functions.invoke('apns-push', { body: { request_id: verzoekId } });
  if (push.error) return { verzoekId, push: null, pushFout: push.error.message ?? String(push.error) };
  return { verzoekId, push: push.data as PushUitkomst, pushFout: null };
}

/** De bytes van één Versie (PDF) via gv-crm-mcp, dezelfde padcontroles als read_project_file. */
export async function haalBestandsinhoud(client: FeedbackClient, projectId: string, bestand: Bestand): Promise<Blob> {
  const data = await roepAan<Blob>(client, 'file_content', {
    project: projectId,
    folder: bestand.folder,
    file_name: bestand.name,
    file_id: bestand.id,
  });
  if (!(data instanceof Blob)) throw new Error('Het bestand kwam niet als bestand terug.');
  return data;
}

/** De CRM-link naar een Versie in de documentweergave (meldingen, taken). */
export function documentLink(projectId: string, fileId: string): string {
  return `/crm/projects/${projectId}?document=${encodeURIComponent(fileId)}`;
}

/** Waar een feedbackmelding naartoe gaat: het verzoek wijst naar project en Versie. */
export async function linkVanVerzoek(client: Pick<FeedbackClient, 'from'>, verzoekId: string): Promise<string | null> {
  const { data } = await client.from('document_feedback_requests').select('project_id, file_id').eq('id', verzoekId).maybeSingle();
  return data ? documentLink(data.project_id, data.file_id) : null;
}

/* ─── Pure hulpjes, gedeeld door Bestanden en de documentweergave ─── */

export type Soort = 'pdf' | 'dia' | 'pagina' | 'blad' | 'overig';

/** Hoe een plek in dit bestand heet: een PDF krijgt punten op de pagina, Office alleen een nummer. */
export function soortVan(naam: string): Soort {
  const ext = /\.([A-Za-z0-9]{1,10})$/.exec(naam)?.[1]?.toLowerCase() ?? '';
  if (ext === 'pdf') return 'pdf';
  if (['pptx', 'ppt', 'key', 'odp'].includes(ext)) return 'dia';
  if (['docx', 'doc', 'odt', 'rtf'].includes(ext)) return 'pagina';
  if (['xlsx', 'xls', 'xlsm', 'ods', 'csv'].includes(ext)) return 'blad';
  return 'overig';
}

export function plekLabel(p: Pick<FeedbackPunt, 'page'>, soort: Soort): string {
  if (!p.page) return 'hele document';
  if (soort === 'dia') return `dia ${p.page}`;
  if (soort === 'blad') return `blad ${p.page}`;
  return `p. ${p.page}`;
}

/** Alle bestands-id's van een document (alle versies en taalvarianten). */
export function bestandenVan(doc: DocumentGroup<Bestand>): Set<string> {
  return new Set(doc.versions.flatMap((v) => v.files.map((f) => f.file.id)));
}

/** De versie van een document waar een bestand in zit; null als het er niet (meer) in zit. */
export function versieVan(doc: DocumentGroup<Bestand>, fileId: string) {
  return doc.versions.find((v) => v.files.some((f) => f.file.id === fileId)) ?? null;
}

export function puntenVan(doc: DocumentGroup<Bestand>, punten: FeedbackPunt[]): FeedbackPunt[] {
  const ids = bestandenVan(doc);
  return punten.filter((p) => ids.has(p.file_id));
}

export function verzoekenVan(doc: DocumentGroup<Bestand>, verzoeken: FeedbackVerzoek[]): FeedbackVerzoek[] {
  const ids = bestandenVan(doc);
  return verzoeken.filter((v) => ids.has(v.file_id));
}

/** Vast nummer per punt binnen een document, op volgorde van plaatsen (1, 2, 3 …). */
export function nummers(punten: FeedbackPunt[]): Map<string, number> {
  const volgorde = [...punten].sort((a, b) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : a.id.localeCompare(b.id)));
  return new Map(volgorde.map((p, i) => [p.id, i + 1]));
}

export function statusLabel(p: Pick<FeedbackPunt, 'status' | 'resolved_version'>): string {
  if (p.status === 'resolved') return p.resolved_version ? `Verwerkt in v${p.resolved_version}` : 'Verwerkt';
  if (p.status === 'dismissed') return 'Niet overgenomen';
  return 'Open';
}

export function telling(punten: FeedbackPunt[]): { open: number; totaal: number } {
  return { open: punten.filter((p) => p.status === 'open').length, totaal: punten.length };
}

/** Open verzoeken aan mij voor dit document (nog niet klaar gemeld). */
export function openVoorMij(verzoeken: FeedbackVerzoek[], mijnId: string | null | undefined): FeedbackVerzoek[] {
  if (!mijnId) return [];
  return verzoeken.filter((v) => v.recipients.some((r) => r.user_id === mijnId && !r.done));
}

export function pushLabel(r: Pick<Ontvanger, 'push_status'>): string {
  switch (r.push_status) {
    case 'sent': return 'push verstuurd';
    case 'no_device': return 'geen iPhone';
    case 'not_configured': return 'push nog niet ingericht';
    case 'failed': return 'push mislukt';
    default: return 'push nog niet verstuurd';
  }
}

/** Een klik (punt) of een sleep (kader) op een pagina, als fractie van de pagina. */
export function ankerUitSleep(
  pagina: number,
  start: { x: number; y: number },
  eind: { x: number; y: number },
  maat: { breedte: number; hoogte: number },
  drempelPx = 8,
): Anker {
  const klem = (v: number) => Math.min(1, Math.max(0, v));
  const dx = (eind.x - start.x) * maat.breedte;
  const dy = (eind.y - start.y) * maat.hoogte;
  if (Math.hypot(dx, dy) < drempelPx) return { page: pagina, x: rond(klem(start.x)), y: rond(klem(start.y)) };
  const x0 = klem(Math.min(start.x, eind.x));
  const y0 = klem(Math.min(start.y, eind.y));
  const x1 = klem(Math.max(start.x, eind.x));
  const y1 = klem(Math.max(start.y, eind.y));
  if (x1 - x0 < 0.002 || y1 - y0 < 0.002) return { page: pagina, x: rond(x0), y: rond(y0) };
  return { page: pagina, x: rond(x0), y: rond(y0), w: rond(x1 - x0), h: rond(y1 - y0) };
}

/** 'Niels en Dylan', 'Niels, Dylan en Lieke'. */
export function namenLijst(namen: string[]): string {
  if (namen.length <= 1) return namen[0] ?? '';
  return `${namen.slice(0, -1).join(', ')} en ${namen.at(-1)}`;
}

/** Wat de ping teweegbracht, in één zin voor de melding na het versturen. */
export function pingSamenvatting(u: PingUitkomst, naam: (id: string) => string): string {
  if (!u.push) return `Melding en taak staan klaar; de push is niet verstuurd (${u.pushFout ?? 'onbekende fout'}).`;
  if (!u.push.configured) return 'Melding en taak staan klaar; pushmeldingen zijn nog niet ingericht (APNs-sleutel).';
  const met = u.push.recipients.filter((r) => r.push_status === 'sent').map((r) => naam(r.user_id));
  const zonder = u.push.recipients.filter((r) => r.push_status !== 'sent').map((r) => naam(r.user_id));
  const delen = [
    met.length ? `push naar ${namenLijst(met)}` : null,
    zonder.length ? `alleen melding en taak voor ${namenLijst(zonder)} (geen iPhone of push mislukt)` : null,
  ].filter(Boolean);
  return `Melding en taak staan klaar; ${delen.join('; ')}.`;
}
